import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getRecord, likeRecord } from '../api/jueGuangService';
import { useLanguage } from '@shared/contexts/LanguageContext';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}. ${dd}. ${yyyy}`;
}

export default function JueGuangDetailPage() {
  const navigate = useNavigate();
  const { recordId } = useParams();
  const { state } = useLocation();
  const { t, currentLanguage } = useLanguage();

  const [post, setPost] = useState(state?.post ?? null);
  const [loading, setLoading] = useState(!state?.post);
  const [liked, setLiked] = useState(state?.post?.is_liked ?? false);
  const [likeCount, setLikeCount] = useState(state?.post?.like_count ?? 0);

  useEffect(() => {
    if (state?.post) return;
    getRecord(Number(recordId)).then(res => {
      if (res.success) {
        setPost(res.data);
        setLiked(res.data.is_liked ?? false);
        setLikeCount(res.data.like_count ?? 0);
      }
      setLoading(false);
    });
  }, [recordId, currentLanguage]);

  async function handleLike() {
    if (liked) return;
    setLiked(true);
    setLikeCount(c => c + 1);
    const res = await likeRecord(post.record_id, true);
    if (res.success) {
      if (res.data.like_count != null) setLikeCount(res.data.like_count);
    } else {
      setLiked(false);
      setLikeCount(c => c - 1);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="text-stone-400 text-sm">{t('jue-guang.loading')}</span>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white gap-4">
        <span className="text-stone-400 text-sm">{t('jue-guang-record.notFound')}</span>
        <button onClick={() => navigate(-1)} className="text-stone-500 text-sm underline cursor-pointer">{t('jue-guang-record.back')}</button>
      </div>
    );
  }

  const imageSrc = post.cache_url || post.thumbnail || null;
  const monastery = post.monastery_name ? `${post.monastery_name} • ` : '';
  const fullName = `${monastery}${post.display_name || post.user_name || ''}`;

  return (
    <div className="min-h-screen bg-white flex flex-col">

      {/* ── Full-bleed image ── */}
      <div className="relative w-full bg-stone-100" style={{ aspectRatio: '1 / 1' }}>
        {imageSrc
          ? <img src={imageSrc} alt="" className="w-full h-full object-cover" />
          : <div className="w-full h-full bg-stone-200" />
        }

        {/* Status bar placeholder */}
        <div className="absolute top-0 left-0 right-0 h-14 bg-white/0" />

        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute left-5 top-14 cursor-pointer p-1"
        >
          <span
            className="material-symbols-outlined drop-shadow-md"
            style={{ fontSize: '24px', color: '#fff', textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}
          >
            arrow_back_ios
          </span>
        </button>

        {/* Title overlay */}
        <div className="absolute left-0 right-0 flex flex-col items-center pointer-events-none" style={{ top: '72px' }}>
          <h1 className="text-2xl font-normal font-['Noto_Serif_TC'] tracking-[4px] text-white leading-7"
            style={{ textShadow: '0 1px 6px rgba(0,0,0,0.35)' }}>
            {t('jue-guang.pageTitle')}
          </h1>
        </div>
      </div>

      {/* ── Info section ── */}
      <div className="flex-1 px-[30px] pt-5 pb-10">

        {/* Date + like row */}
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-['SF_Pro'] leading-7 text-black">
            {formatDate(post.created_at)}
          </p>
          <button
            onClick={handleLike}
            className="shrink-0 flex items-center gap-1 cursor-pointer transition-all active:scale-90 ml-2"
          >
            <span
              className="material-symbols-outlined transition-colors"
              style={{
                fontSize: '24px',
                color: liked ? '#e53e3e' : '#1c1b1f',
                fontVariationSettings: liked ? "'FILL' 1" : "'FILL' 0",
              }}
            >
              favorite
            </span>
            {likeCount > 0 && (
              <span className="text-sm text-stone-500 tabular-nums">{likeCount}</span>
            )}
          </button>
        </div>

        {/* User row */}
        <div className="flex items-center gap-2 min-w-0 mb-3">
          <div className="w-9 h-9 rounded-full bg-[#c5c5c5] overflow-hidden shrink-0 flex items-center justify-center">
            {post.mascot_url
              ? <img src={post.mascot_url} alt="" className="w-9 h-9 object-cover" />
              : <span className="material-symbols-outlined text-white" style={{ fontSize: '18px' }}>person</span>
            }
          </div>
          <span className="text-base font-['SF_Pro'] leading-7 text-black truncate">
            {fullName}
          </span>
        </div>

        {/* Description */}
        {post.description && (
          <p className="text-base font-normal font-['SF_Pro'] leading-6 text-black mb-4">
            {post.description}
          </p>
        )}

        {/* Divider */}
        <div className="w-full h-px bg-black" />
      </div>
    </div>
  );
}
